import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ACCESS_SEGMENTS, AccessSegment, HomeShortsFeed, UserLevel } from '../../../../../api/homeShortsFeeds/types';
import { ACCESS_LABELS, DEFAULT_FEED_ID, levelLabel } from '../utils';

const EMPTY_START = 'empty';

export interface FeedModalValues {
  name: string;
  levels: number[];
  access: AccessSegment[];
  /** 'empty', DEFAULT_FEED_ID or another feed's id. Only used when creating. */
  startFrom: string;
}

interface Props {
  open: boolean;
  /** undefined = create, object = edit */
  feed?: HomeShortsFeed;
  levels: UserLevel[];
  otherFeeds: HomeShortsFeed[];
  onClose: () => void;
  onSave: (values: FeedModalValues) => void;
}

// Toggle chips: the pressed state is announced with aria-pressed and shown with a check mark.
const ToggleChip = ({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) => (
  <Chip
    label={selected ? `✓ ${label}` : label}
    color={selected ? 'primary' : 'default'}
    variant={selected ? 'filled' : 'outlined'}
    onClick={onClick}
    aria-pressed={selected}
  />
);

const toggleValue = <T,>(list: T[], value: T) =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

// Create / edit a feed: name plus who it targets. No selection means any.
const FeedModal = ({ open, feed, levels, otherFeeds, onClose, onSave }: Props) => {
  const [name, setName] = useState('');
  const [selectedLevels, setSelectedLevels] = useState<number[]>([]);
  const [selectedAccess, setSelectedAccess] = useState<AccessSegment[]>([]);
  const [startFrom, setStartFrom] = useState(EMPTY_START);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (open) {
      setName(feed?.name ?? '');
      setSelectedLevels(feed?.levels ?? []);
      setSelectedAccess(feed?.access ?? []);
      setStartFrom(EMPTY_START);
      setShowError(false);
    }
  }, [open, feed]);

  const trimmedName = name.trim();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmedName) {
      setShowError(true);
      return;
    }
    onSave({
      name: trimmedName,
      levels: [...selectedLevels].sort((a, b) => a - b),
      access: ACCESS_SEGMENTS.filter((item) => selectedAccess.includes(item)),
      startFrom,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <form onSubmit={submit} noValidate>
        <DialogTitle>{feed ? 'Edit feed' : 'New feed'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              size='small'
              fullWidth
              autoFocus
              label='Name'
              placeholder='e.g. New free users'
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={showError && !trimmedName}
              helperText={showError && !trimmedName ? 'Name is required' : undefined}
            />

            <Box>
              <Typography variant='subtitle2' mb={0.5}>
                Levels
              </Typography>
              <Typography variant='caption' color='text.secondary' component='p' mb={1}>
                {selectedLevels.length === 0 ? 'Any level (nothing selected).' : 'Only users on the selected levels.'}
              </Typography>
              <Stack direction='row' gap={1} flexWrap='wrap' role='group' aria-label='Levels'>
                {levels.map((level) => (
                  <ToggleChip
                    key={level.level}
                    label={levelLabel(level)}
                    selected={selectedLevels.includes(level.level)}
                    onClick={() => setSelectedLevels((current) => toggleValue(current, level.level))}
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant='subtitle2' mb={0.5}>
                Access
              </Typography>
              <Typography variant='caption' color='text.secondary' component='p' mb={1}>
                {selectedAccess.length === 0
                  ? 'Any access (nothing selected).'
                  : 'Only users with the selected status.'}
              </Typography>
              <Stack direction='row' gap={1} flexWrap='wrap' role='group' aria-label='Access'>
                {ACCESS_SEGMENTS.map((access) => (
                  <ToggleChip
                    key={access}
                    label={ACCESS_LABELS[access]}
                    selected={selectedAccess.includes(access)}
                    onClick={() => setSelectedAccess((current) => toggleValue(current, access))}
                  />
                ))}
              </Stack>
            </Box>

            {!feed && (
              <FormControl size='small' fullWidth>
                <InputLabel id='feed-start-from-label'>Start from</InputLabel>
                <Select
                  labelId='feed-start-from-label'
                  label='Start from'
                  value={startFrom}
                  onChange={(e) => setStartFrom(e.target.value)}>
                  <MenuItem value={EMPTY_START}>Empty</MenuItem>
                  <MenuItem value={DEFAULT_FEED_ID}>Copy Default</MenuItem>
                  {otherFeeds.map((other) => (
                    <MenuItem key={other.id} value={other.id}>
                      Copy “{other.name}”
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type='submit' variant='contained'>
            {feed ? 'Update feed' : 'Add feed'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FeedModal;
